import React from 'react'
import {
    TableHead,
    TableSortLabel,
    TableRow,
    TableCell,
    Link,
    Box,
    styled,
    Table,
} from '@mui/material'
import { Tooltip, StatusIndicator, RiskIndicator } from 'packages/eid-ui'
import { usePageContext } from 'pageContext'
import { useTranslation } from 'react-i18next'
import {
    TextFormatter,
    Person,
    ItemTypeCount,
    SubjectTypeAttribute,
} from 'components'
import { useHistory } from 'react-router-dom'
import { useQuery } from 'packages/core'
import moment from 'moment'
import { getFullImageUrl } from 'utils'
import ItemTypeResourceSummary from 'components/ItemTypeResourceSummary'
import appConfig from 'config'
const maxCharacters = 100

const colors = {
    primary: '#307fc1',
    grey: '#7d7c7c',
    black: '#282828',
}

const EmptyRow = styled(TableRow)({
    border: 'none !important',
})
const EmptyCell = styled(TableCell)({
    border: 'none !important',
    padding: '9px',
})

const StyledHeaderTableRow = styled(TableRow)({
    backgroundColor: '#fbfbfd',

    height: '3.2rem',
    borderRadius: '0.8rem !important',

    boxShadow: ' 0 0.2rem 0.8rem 0 rgba(0, 0, 0, 0.05)',

    '& >th:first-child': {
        borderBottomLeftRadius: '0.4rem !important',
        borderTopLeftRadius: '0.4rem !important',
    },
    '& >th:last-child': {
        borderBottomRightRadius: '0.4rem !important',
        borderTopRightRadius: '0.4rem !important',
    },

    '& .MuiTableCell-head': {
        padding: '1rem ',
        fontSize: '1.3rem',
        lineHeight: 1.33,
        color: colors.grey,
    },
})

const StyledSortLabel = styled(TableSortLabel)({
    color: '#7d7c7c !important',
    '& svg': {
        color: '#D2D2D9 !important',
        height: '1.4rem',
        width: '1.4rem',
    },
    '&:hover': {
        color: `${colors.primary} !important`,
        '& svg': {
            color: `${colors.primary} !important`,
        },
    },
    '&.Mui-active': {
        '& svg': {
            color: `${colors.primary} !important`,
        },
    },
    '& .MuiTableSortLabel-icon': {
        opacity: '1 !important',
    },
})

const StyledHeaderCell = styled(TableCell)({
    border: 'none !important',
    padding: '16px 10px',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    overflow: 'hidden',
})

const StyledCell = styled(TableCell)(({ style }) => ({
    border: 'none ',
    padding: '1.6rem 0rem',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    overflow: 'hidden',
    color: colors.black,
    fontSize: '1.3rem',
    lineHeight: 1.5,
    '& >a': {
        color: style && style.color ? style.color : colors.primary,
    },
    textDecoration: 'underline',
    ...style,
}))

const tooltipProps = {
    color: '#5D6870',
    fontColor: '#ffffff',
    enterDelay: 500,
    enterNextDelay: 500,
    disableInteractive: false,
    tooltipStyles: {
        boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
        padding: '8px',
        maxWidth: '500px',
    },
}

const CustomTableCell = ({ style, link, children, onClick, url, ...rest }) => {
    return (
        <StyledCell align="left" onClick={onClick} {...rest} style={style}>
            {!link ? (
                children
            ) : (
                <Link
                    href={url}
                    onClick={(e) => {
                        e.stopPropagation()
                    }}
                >
                    {children}
                </Link>
            )}
        </StyledCell>
    )
}

export const ReportsTableHead = ({ attributes }) => {
    const { t } = useTranslation()
    const [{ sortBy, sortOrder }, dispatch] = usePageContext()

    const handleSort = (heading) => {
        const sortByName = heading.sortBy ? heading.sortBy : heading.name
        dispatch({
            type: 'SET_MULTIPLE_PROPS',
            payload: {
                sortBy: sortByName,
                sortOrder:
                    sortBy !== sortByName
                        ? 'desc'
                        : !sortOrder || sortOrder === '' || sortOrder === 'asc'
                        ? 'desc'
                        : 'asc',
            },
        })
    }

    return (
        <TableHead>
            <>
                <StyledHeaderTableRow>
                    {attributes.map((heading, index) => {
                        const sortByName = heading.sortBy
                            ? heading.sortBy
                            : heading.name
                        return (
                            <StyledHeaderCell
                                key={'pd-head-cell-' + index}
                                align="left"
                                style={heading.headerCellStyles}
                            >
                                {!heading.sortable ? (
                                    <span
                                        style={{
                                            display: 'flex',
                                        }}
                                    >
                                        {t(heading.label)}
                                    </span>
                                ) : (
                                    <span style={{ display: 'flex' }}>
                                        <Tooltip
                                            title={
                                                sortBy === sortByName &&
                                                sortOrder === 'desc'
                                                    ? t('SortAscending')
                                                    : t('SortDescending')
                                            }
                                        >
                                            <StyledSortLabel
                                                active={sortBy === sortByName}
                                                direction={
                                                    sortBy === sortByName
                                                        ? sortOrder
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort(heading)
                                                }
                                            >
                                                {t(heading.label)}
                                            </StyledSortLabel>
                                        </Tooltip>
                                    </span>
                                )}
                            </StyledHeaderCell>
                        )
                    })}
                </StyledHeaderTableRow>
                <EmptyRow>
                    <EmptyCell colSpan={attributes.length} />
                </EmptyRow>
            </>
        </TableHead>
    )
}

const StyledRowClasses = styled(TableRow)({
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    borderTopLeftRadius: '0.8rem !important',
    borderTopRightRadius: '0.8rem !important',
    minHeight: '4rem',

    '& >td:first-child': {
        borderBottomLeftRadius: '0rem !important',
        borderTopLeftRadius: '0.8rem !important',
    },
    '& >td:last-child': {
        borderBottomRightRadius: '0rem !important',
        borderTopRightRadius: '0.8rem !important',
    },

    '&:hover': {
        '& .Details-button': {
            backgroundColor: colors.primary,
            color: '#ffffff',
            cursor: 'pointer',
        },
    },
})

const DetailsButton = ({ styles, onClick }) => (
    <span style={styles}>
        <StyledLink
            className="Details-button"
            component="button"
            onClick={(e) => {
                onClick()
                e.stopPropagation()
            }}
        >
            +
        </StyledLink>
    </span>
)

const StyledLink = styled(Link)({
    borderRadius: '50%',
    fontWeight: 'normal',
    width: '20px',
    height: '20px',
    backgroundColor: '#ffffff',
    border: '1px solid #aab0b4',
    color: '#aab0b4',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '&:hover': {
        backgroundColor: colors.primary,
        color: '#ffffff',
        cursor: 'pointer',
    },

    '&:focus': {
        outline: 'none',
    },
})

const SecondRowCell = styled(TableCell)({
    padding: '10px 10px',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    overflow: 'hidden',
    color: colors.grey,
    borderRadius: '8px !important',
    borderImageSource:
        'linear-gradient(to right, rgba(120, 48, 197, 0), #7830c5 7%, #c530a9 91%, rgba(197, 48, 169, 0))',
    borderImageSlice: 1,
    //boxShadow: 'inset 0 0 10px -10px #000000',
})

const StyledSecondRow = styled(TableRow)({
    backgroundColor: '#ffffff',

    minHeight: '400px',

    boxShadow: 'inset 0 6px 16px -16px rgba(0, 0, 0, 0.5)',
    '& >td:first-child': {
        borderBottomLeftRadius: '8px !important',

        borderTopLeftRadius: '0px !important',
    },
    '& >td:last-child': {
        borderBottomRightRadius: '8px !important',
        borderTopRightRadius: '0px !important',
    },

    '&:hover': {
        '& .Details-button': {
            backgroundColor: colors.primary,
            color: '#ffffff',
            cursor: 'pointer',
        },
    },
})

const CellDateTime = ({ value }) => (
    <>{value ? moment.utc(value).local().format('LLL') : '-'}</>
)

const Attribute = styled('span')({
    display: 'flex',
    alignItems: 'center',
    padding: '0 10px',
    fontSize: '13px !important',
    '& >span': {
        margin: '0px 6px 0px 10px',
        fontWeight: 'bold',
    },
    '& >svg': {
        marginBottom: '1px',
    },
})

const StyledTable = styled(Table)({
    border: 'none !important',
    boxShadow: 'none !important',
    borderCollapse: 'separate',
})

export const ReportsTable = StyledTable

export const ReportsTableRow = ({ attributes, item }) => {
    const { t } = useTranslation()

    const renderButton = (a) => {
        switch (a.button.name) {
            case 'DetailsButton':
                return (
                    <DetailsButton
                        onClick={() => {}}
                        styles={a.button.styles}
                    />
                )

            default:
                return <></>
        }
    }

    const renderComponent = (a, item) => {
        switch (a.component.componentName) {
            case 'StatusIndicator':
                const itemStatus = item.preApproved
                    ? 'PreApproved'
                    : item[a.component.name]
                return (
                    <StatusIndicator
                        className="Status-Indicator"
                        style={a.component.styles}
                        variant={itemStatus}
                        label={t(`${itemStatus}`)}
                        {...a.component.props}
                    />
                )
            case 'RiskIndicator':
                return (
                    <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                    >
                        {item[a.component.name] ? (
                            <RiskIndicator
                                variant={item[a.component.name]}
                                label={t(`${item[a.component.name]}Risk`)}
                                {...a.component.props}
                            />
                        ) : (
                            <>-</>
                        )}
                    </Box>
                )

            case 'CellDateTime':
                return <CellDateTime value={item[a.name]} />

            case 'SubjectType':
                const subjectType = item.subjectType
                return (
                    <SubjectTypeAttribute
                        subjectType={subjectType}
                        item={item}
                        personComponentProps={{ ...a.component.props }}
                    />
                )

            default:
                return <></>
        }
    }

    return (
        <React.Fragment>
            <StyledRowClasses>
                {attributes.map((a, index) => {
                    return (
                        <CustomTableCell
                            key={item.id + `${index}`}
                            style={a.styles}
                            url={a.url}
                            link={a.link}
                            onClick={() => {
                                window.open(
                                    `${appConfig.APP_SUBPATH}/all/businessRequestItems?id=${item.businessRequestItemId}`,
                                )
                            }}
                        >
                            <Box
                                display="inline-block"
                                alignItems="center"
                                paddingX="10px"
                                width={a.styles?.width}
                            >
                                {a.avatar && (
                                    <Person
                                        name={item[a.name]}
                                        imgSrc={getFullImageUrl(
                                            item[a.imgProp],
                                        )}
                                        tooltipProps={tooltipProps}
                                        style={a?.textStyles}
                                    />
                                )}

                                {!a.hideValue && (
                                    <TextFormatter
                                        value={item[a.name]}
                                        tooltipProps={tooltipProps}
                                        style={a.styles}
                                        maxCharacters={maxCharacters}
                                    ></TextFormatter>
                                )}

                                {a.button && renderButton(a)}

                                {a.component && renderComponent(a, item)}
                            </Box>
                        </CustomTableCell>
                    )
                })}
            </StyledRowClasses>
            {item.itemTypeCount && item.itemTypeResourceSummary && (
                <StyledSecondRow>
                    <SecondRowCell colSpan={attributes.length}>
                        <Box
                            component="span"
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box display="flex" justifyContent="flex-start">
                                <ItemTypeResourceSummary
                                    itemTypeCount={item.itemTypeCount}
                                    itemTypeResourceSummary={
                                        item.itemTypeResourceSummary
                                    }
                                />
                            </Box>

                            {item.currentApprovalStepName && (
                                <Attribute key={item.status}>
                                    <span>{t('CurrentStep')}</span>
                                    {item.currentApprovalStepName}
                                </Attribute>
                            )}
                        </Box>
                    </SecondRowCell>
                </StyledSecondRow>
            )}
            <EmptyRow>
                <EmptyCell colSpan={attributes.length} />
            </EmptyRow>
        </React.Fragment>
    )
}
